import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Fab,
  Badge,
  Snackbar,
  Button,
  Breadcrumbs,
  Link as MuiLink,
  IconButton,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Refresh,
  ArrowBack,
  VideoLibrary,
  CameraAlt,
  Videocam,
  AdminPanelSettings,
  Schedule,
  PlayArrow
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AccountWithCameras, RecordingResponse, RecordingInfo, recordingApi } from '../../api/recordingApi';
import AccountCameraList from '../../components/AccountCameraList';
import ScheduledRecordingsList from '../../components/ScheduledRecordingsList';
import ComprehensiveClipView from '../../components/ComprehensiveClipView';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

const AdminCameraRecording: React.FC = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<AccountWithCameras[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allRecordings, setAllRecordings] = useState<RecordingInfo[]>([]);
  const [notification, setNotification] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    loadCameras();
    loadRecordings();
    
    // Refresh recordings every 30 seconds
    const interval = setInterval(() => {
      loadRecordings();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadCameras = async () => {
    try {
      setLoading(true);
      const accountsWithCameras = await recordingApi.getCamerasGroupedByAccount();
      setAccounts(accountsWithCameras);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cameras');
      console.error('Failed to load cameras:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRecordings = async () => {
    try {
      const recordings = await recordingApi.getRecordings();
      setAllRecordings(recordings);
    } catch (err) {
      console.error('Failed to load recordings:', err);
    }
  };

  const handleRefresh = () => {
    loadCameras();
    loadRecordings();
  };

  const getTotalCameras = () => {
    return accounts.reduce((total, account) => total + account.cameras.length, 0);
  };

  const getActiveRecordingsCount = () => {
    return allRecordings.filter(recording => recording.status === 'recording').length;
  };



  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('de-DE');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
        <MuiLink color="inherit" href="/dashboard">
          Dashboard
        </MuiLink>
        <MuiLink color="inherit" href="/backoffice">
          Administration
        </MuiLink>
        <Typography color="text.primary">Kamera-Aufnahmen</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            <AdminPanelSettings sx={{ mr: 2 }} />
            Kamera-Aufnahmen
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/backoffice')}
          >
            Zurück
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange} 
          aria-label="Admin Aufnahmen Tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            label="Geplante Aufnahmen" 
            icon={<Schedule />} 
            iconPosition="start"
            {...a11yProps(0)} 
          />
          <Tab 
            label="Neue Aufnahmen planen" 
            icon={<PlayArrow />} 
            iconPosition="start"
            {...a11yProps(1)} 
          />
          <Tab 
            label="Alle Clips" 
            icon={<VideoLibrary />} 
            iconPosition="start"
            {...a11yProps(2)} 
          />
        </Tabs>
      </Box>

      {/* Tab 1: Geplante Aufnahmen anzeigen */}
      <TabPanel value={selectedTab} index={0}>
        {/* Scheduled Recordings List */}
        <ScheduledRecordingsList 
          accounts={accounts}
          loading={loading}
          error={error}
          onRefresh={handleRefresh}
          totalCameras={getTotalCameras()}
          totalRecordings={allRecordings.length}
        />
      </TabPanel>

      {/* Tab 2: Neue Aufnahmen planen */}
      <TabPanel value={selectedTab} index={1}>
        {/* Account Camera List */}
        <AccountCameraList
          accounts={accounts}
          loading={loading}
          error={error}
          onRefresh={handleRefresh}
          onScheduleSuccess={handleRefresh}
        />

        {/* Recent Recordings Table */}
      </TabPanel>

      {/* Tab 3: Alle Clips */}
      <TabPanel value={selectedTab} index={2}>
        <ComprehensiveClipView />
      </TabPanel>

      {/* Notifications */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        message={notification}
      />
    </Container>
  );
};

export default AdminCameraRecording; 